/**
 * Graphical requests API documentation — English (also the fallback of the
 * languages that have no translation). Sections are HTML; {{sample:x}} is a
 * code sample of samples.js, {{formats}} the output format table, {{BASE}} the
 * API base URL. Keep the four languages (en, fr, nl, de) in step.
 */
/* jshint maxlen: 1000 */
// Prose: one paragraph per line, longer than the usual 250 characters.
define('apiDocsContentEn',[], function () {
  'use strict';

  return {
    ui: {
      title: 'Graphical requests API',
      subtitle: 'Send vectorizations, file conversions, background removals and colorizations from your own software, and get the files back automatically.',
      tabDocs: 'Documentation',
      tabKeys: 'API keys & webhook',
      onThisPage: 'On this page',
      docLanguage: 'Language of the documentation',
      copy: 'Copy',
      copied: 'Copied',
      baseUrl: 'Base URL',
      keys: {
        title: 'API keys',
        intro: 'A key lets a program act for your account. Create one key per integration (webshop, ERP, script...) so you can revoke one without stopping the others. Calls made with a key are made in the name of the person who created it.',
        name: 'Name of the key',
        namePlaceholder: 'e.g. Webshop production',
        create: 'Create a key',
        createdTitle: 'Your new key',
        createdHint: 'Copy it now and keep it somewhere safe: it is shown only once. If you lose it, revoke it and create a new one.',
        prefix: 'Key',
        createdBy: 'Created by',
        createdAt: 'Created on',
        lastUsed: 'Last used',
        never: 'Never',
        status: 'Status',
        active: 'Active',
        revoked: 'Revoked',
        revoke: 'Revoke',
        revokeConfirm: 'Revoke this key? Programs that use it will be refused at once. This cannot be undone.',
        empty: 'No key yet. Create one to start using the API.',
        error: 'The operation failed. Please try again.'
      },
      webhook: {
        title: 'Webhook',
        intro: 'Instead of asking regularly whether a request is ready, give us a URL: we call it at every change of every graphical request of your account (created, waiting for you, delivered, closed...). Each call is signed with the secret below.',
        url: 'URL of your endpoint (https)',
        urlPlaceholder: 'https://www.your-company.com/hooks/compuzz',
        save: 'Save',
        saved: 'Webhook saved.',
        secret: 'Signing secret',
        show: 'Show',
        hide: 'Hide',
        rotate: 'New secret',
        rotateConfirm: 'Create a new secret? Signatures made with the old one will no longer match: update your endpoint right away.',
        ping: 'Send a test event',
        pingSent: 'A "ping" event is on its way (within about a minute).',
        remove: 'Remove',
        removeConfirm: 'Remove the webhook? We will stop calling your endpoint.',
        none: 'No webhook set.',
        events: 'Events sent'
      }
    },

    sections: [
      {
        id: 'overview',
        title: 'Overview',
        body: [
          '<p>The graphical requests API gives your systems the same service as the <em>Graphical requests</em> screens of the dashboard: you send a file (a logo in JPG, a low-resolution picture, a PDF...), our graphic designers prepare it, and you get the result back in the format you need.</p>',
          '<div class="api-doc-cards">',
          '  <div class="api-doc-card"><i class="fa fa-pencil-square-o"></i><b>vectorization</b><span>A bitmap logo redrawn as clean vectors (AI, EPS, PDF, SVG, CDR...).</span></div>',
          '  <div class="api-doc-card"><i class="fa fa-exchange"></i><b>conversion</b><span>A file converted to another format or version.</span></div>',
          '  <div class="api-doc-card"><i class="fa fa-scissors"></i><b>remove_background</b><span>The background of a picture removed.</span></div>',
          '  <div class="api-doc-card"><i class="fa fa-paint-brush"></i><b>colorization</b><span>A design recoloured to the colours you ask for.</span></div>',
          '</div>',
          '<p>A request made through the API is an ordinary graphical request: you also see it in the dashboard, you can follow the conversation there, and it costs the same credits.</p>',
          '<ul class="api-doc-facts">',
          '  <li><b>REST + JSON</b>, files sent as <code>multipart/form-data</code> (or base64 in JSON).</li>',
          '  <li><b>Base URL</b>: <code>{{BASE}}</code></li>',
          '  <li><b>Authentication</b>: an API key of your account, in the <code>Authorization</code> header.</li>',
          '  <li><b>Dates</b> in ISO 8601, UTC (<code>2026-10-22T13:40:51Z</code>). <b>Ids</b> are strings: store them as they are.</li>',
          '  <li><b>Webhooks</b> tell you when something happens, so you do not have to poll.</li>',
          '</ul>'
        ].join('\n')
      },
      {
        id: 'quickstart',
        title: 'Quick start',
        body: [
          '<ol class="api-doc-steps">',
          '  <li><b>Create a key</b> in the <a href="#api/graphical-requests/keys">API keys &amp; webhook</a> tab and store it in your configuration (for example in the environment variable <code>COMPUZZ_API_KEY</code>).</li>',
          '  <li><b>Send a file</b>: <code>POST /graphical-requests</code> with the type and the file. You get the request back with its <code>id</code> and the <code>status</code> <code>submitted</code>.</li>',
          '  <li><b>Wait for the result</b>: the <code>request.delivered</code> event reaches your webhook (or read <code>GET /graphical-requests/{id}</code> from time to time).</li>',
          '  <li><b>Download</b> the files whose <code>role</code> is <code>result</code> and <code>latest</code> is <code>true</code>.</li>',
          '  <li><b>Accept</b> the result (<code>POST /graphical-requests/{id}/accept</code>) or ask for a correction with a message.</li>',
          '</ol>',
          '{{sample:create}}',
          '<p>The answer is the request (<code>201 Created</code>):</p>',
          '{{sample:response}}'
        ].join('\n')
      },
      {
        id: 'authentication',
        title: 'Authentication',
        body: [
          '<p>Every call carries an API key of your account, created in the <a href="#api/graphical-requests/keys">API keys &amp; webhook</a> tab:</p>',
          '<pre class="api-doc-inline">Authorization: Bearer cpz_live_XXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX</pre>',
          '<p>The <code>X-Api-Key: cpz_live_...</code> header is accepted too. Keys of our test platform start with <code>cpz_test_</code>.</p>',
          '{{sample:auth}}',
          '<div class="api-doc-note warning"><i class="fa fa-lock"></i><div><b>Keep your keys secret.</b> A key gives access to the graphical requests and the credits of your account. Use it only from your servers, never in a web page or a mobile app. We store only a fingerprint of it: it is shown once, when you create it. A key that leaked must be revoked at once (it stops working immediately).</div></div>',
          '<h4>Rate limit</h4>',
          '<p>Each key can make <b>120 calls per minute</b>. Every answer carries <code>X-RateLimit-Limit</code> and <code>X-RateLimit-Remaining</code>; beyond the limit the answer is <code>429</code> with a <code>Retry-After</code> header (seconds to wait).</p>'
        ].join('\n')
      },
      {
        id: 'lifecycle',
        title: 'Life of a request',
        body: [
          '<div class="api-doc-flow">',
          '  <span class="st st-submitted">submitted</span><i class="fa fa-long-arrow-right"></i>',
          '  <span class="st st-processing">processing</span><i class="fa fa-long-arrow-right"></i>',
          '  <span class="st st-delivered">delivered</span><i class="fa fa-long-arrow-right"></i>',
          '  <span class="st st-closed">closed</span>',
          '</div>',
          '<table class="api-doc-table">',
          '  <thead><tr><th>status</th><th>Meaning</th><th>What you can do</th></tr></thead>',
          '  <tbody>',
          '    <tr><td><span class="st st-submitted">submitted</span></td><td>Received, waiting for a graphic designer.</td><td>Send a message, cancel.</td></tr>',
          '    <tr><td><span class="st st-processing">processing</span></td><td>A graphic designer is working on it (or checking it).</td><td>Send a message.</td></tr>',
          '    <tr><td><span class="st st-action">action_required</span></td><td>We need you: a question, or a new price because the work is more complex than standard (see <a href="#api/graphical-requests/action-required">Waiting for you</a>).</td><td>Accept the quote, answer, cancel.</td></tr>',
          '    <tr><td><span class="st st-delivered">delivered</span></td><td>The result files are ready.</td><td>Download, accept, or ask for a correction.</td></tr>',
          '    <tr><td><span class="st st-closed">closed</span></td><td>Finished. <b>The credits are debited when the request is closed.</b></td><td>Download.</td></tr>',
          '    <tr><td><span class="st st-canceled">canceled</span></td><td>Canceled, nothing is charged.</td><td>-</td></tr>',
          '  </tbody>',
          '</table>',
          '<p>When you ask for a correction on a delivered request, the graphic designer takes it back (<code>processing</code>) and delivers again. Requests left in <code>action_required</code> for a long time may be canceled by our team.</p>'
        ].join('\n')
      },
      {
        id: 'create',
        title: 'Create a request',
        body: [
          '<p class="api-doc-endpoint"><span class="verb post">POST</span><code>/graphical-requests</code></p>',
          '<p>Send <code>multipart/form-data</code> with these fields and one or more <code>file</code> parts (at most 10 files, 150 MB in total):</p>',
          '<table class="api-doc-table">',
          '  <thead><tr><th>Field</th><th></th><th>Description</th></tr></thead>',
          '  <tbody>',
          '    <tr><td><code>type</code></td><td class="req">required</td><td><code>vectorization</code>, <code>conversion</code>, <code>remove_background</code> or <code>colorization</code>.</td></tr>',
          '    <tr><td><code>file</code></td><td class="req">required</td><td>The file(s) to work on: the logo, the picture, the original design.</td></tr>',
          '    <tr><td><code>name</code></td><td>optional</td><td>A title (max 250 characters), e.g. the customer and the order. The result files are named after it. Default: the name of the first file.</td></tr>',
          '    <tr><td><code>outputFormat</code></td><td>optional</td><td>The format you want back, e.g. <code>ai_cc</code>, <code>pdf_1_7</code>, <code>eps_cc</code>, <code>svg_1_1</code>, <code>png</code>: see <a href="#api/graphical-requests/reference">the list</a>.</td></tr>',
          '    <tr><td><code>colors</code></td><td>optional</td><td>The colours of the result, in your words: "2 colours: Pantone 485 C and black", "full colour", "white on dark background".</td></tr>',
          '    <tr><td><code>comment</code></td><td>optional</td><td>Anything the graphic designer must know.</td></tr>',
          '    <tr><td><code>urgent</code></td><td>optional</td><td><code>true</code> for priority handling (an urgent supplement is added to the price). Default <code>false</code>.</td></tr>',
          '    <tr><td><code>externalRef</code></td><td>optional</td><td>Your own reference (order number, item id...), max 100 characters. Returned with the request and usable as a filter.</td></tr>',
          '    <tr><td><code>requester</code></td><td>optional</td><td>E-mail address that receives the e-mails about this request. Default: the person who created the key.</td></tr>',
          '  </tbody>',
          '</table>',
          '<p>The same fields can be sent as JSON, with the files in base64:</p>',
          '{{sample:createJson}}',
          '<h4>Retrying safely</h4>',
          '<p>Add an <code>Idempotency-Key</code> header (any unique string up to 100 characters, e.g. your order line). If the call is sent twice with the same key - after a timeout for instance - the second call returns the request created the first time (<code>200</code> instead of <code>201</code>) and nothing is created twice.</p>',
          '<div class="api-doc-note"><i class="fa fa-info-circle"></i><div>A request can be created as long as the <code>available</code> credits of the account are above zero; otherwise the answer is <code>402 insufficient_credits</code>. See <a href="#api/graphical-requests/credits">Credits</a>.</div></div>'
        ].join('\n')
      },
      {
        id: 'retrieve',
        title: 'Read requests',
        body: [
          '<p class="api-doc-endpoint"><span class="verb get">GET</span><code>/graphical-requests/{id}</code></p>',
          '<p>Returns the request: its status, its quote and all its files.</p>',
          '{{sample:get}}',
          '<p class="api-doc-endpoint"><span class="verb get">GET</span><code>/graphical-requests</code></p>',
          '<p>Lists the requests of your account, newest first, including those made in the dashboard (<code>source</code> = <code>dashboard</code>). Filters, all optional:</p>',
          '<table class="api-doc-table">',
          '  <tbody>',
          '    <tr><td><code>status</code></td><td>One status, e.g. <code>delivered</code>.</td></tr>',
          '    <tr><td><code>type</code></td><td>One type, e.g. <code>vectorization</code>.</td></tr>',
          '    <tr><td><code>externalRef</code></td><td>The requests created with this reference.</td></tr>',
          '    <tr><td><code>createdFrom</code>, <code>createdTo</code></td><td>Creation date range (ISO 8601).</td></tr>',
          '    <tr><td><code>updatedSince</code></td><td>Requests changed since this date: the easy way to catch up after an outage of your webhook endpoint.</td></tr>',
          '    <tr><td><code>limit</code></td><td>1 to 100, default 20.</td></tr>',
          '    <tr><td><code>startingAfter</code></td><td>The <code>nextCursor</code> of the previous page.</td></tr>',
          '  </tbody>',
          '</table>',
          '<p>The answer is <code>{"object": "list", "data": [...], "hasMore": true, "nextCursor": "..."}</code>.</p>',
          '{{sample:list}}',
          '<p class="api-doc-endpoint"><span class="verb get">GET</span><code>/graphical-requests/{id}/messages</code></p>',
          '<p>The conversation with the graphic designers, oldest first. Each message has an <code>author</code> (<code>customer</code> or <code>compuzz</code>), a <code>text</code> and its <code>files</code>.</p>'
        ].join('\n')
      },
      {
        id: 'action-required',
        title: 'Waiting for you',
        body: [
          '<p>A request goes to <code>action_required</code> when the graphic designer needs you:</p>',
          '<ul>',
          '  <li><b>A new quote</b>: the design is more complex than a standard one (many details, many colours, a whole illustration...). The <code>tariffScale</code> of the <code>quote</code> goes up (2 = twice the standard price) and <code>totalCredits</code> shows the new total. You receive the <code>request.quote_updated</code> event.</li>',
          '  <li><b>A question</b>: read the last message of <code>GET /graphical-requests/{id}/messages</code>.</li>',
          '</ul>',
          '<table class="api-doc-table">',
          '  <tbody>',
          '    <tr><td><span class="verb post">POST</span> <code>/graphical-requests/{id}/quote/accept</code></td><td>You agree with the quote: the work goes on.</td></tr>',
          '    <tr><td><span class="verb post">POST</span> <code>/graphical-requests/{id}/messages</code></td><td>Answer: field <code>text</code> and optional <code>file</code> parts (or JSON <code>{"text": "...", "files": [...]}</code>).</td></tr>',
          '    <tr><td><span class="verb post">POST</span> <code>/graphical-requests/{id}/cancel</code></td><td>You give up: the request is canceled and nothing is charged.</td></tr>',
          '  </tbody>',
          '</table>',
          '{{sample:quote}}'
        ].join('\n')
      },
      {
        id: 'results',
        title: 'Download the results',
        body: [
          '<p>Every file of a request is in its <code>files</code> list:</p>',
          '<ul>',
          '  <li><code>role</code> = <code>source</code>: a file you sent;</li>',
          '  <li><code>role</code> = <code>result</code>: a file delivered by our graphic designers. After a correction there can be several deliveries: the files of the last one have <code>latest</code> = <code>true</code>.</li>',
          '</ul>',
          '<p class="api-doc-endpoint"><span class="verb get">GET</span><code>/graphical-requests/{id}/files/{fileId}</code></p>',
          '<p>Downloads the file (the <code>downloadUrl</code> of the file, with your key). The answer is the file itself, with its name in <code>Content-Disposition</code>.</p>',
          '{{sample:download}}'
        ].join('\n')
      },
      {
        id: 'accept-cancel',
        title: 'Accept, correct, cancel',
        body: [
          '<table class="api-doc-table">',
          '  <tbody>',
          '    <tr><td><span class="verb post">POST</span> <code>/graphical-requests/{id}/accept</code></td><td>On a <code>delivered</code> request: the result is approved, the request is <code>closed</code> and the credits are debited.</td></tr>',
          '    <tr><td><span class="verb post">POST</span> <code>/graphical-requests/{id}/messages</code></td><td>On a <code>delivered</code> request: ask for a correction. Say precisely what must change, attach a marked-up picture if it helps.</td></tr>',
          '    <tr><td><span class="verb post">POST</span> <code>/graphical-requests/{id}/cancel</code></td><td>On a <code>submitted</code> or <code>action_required</code> request only: once the work has started, write to us instead.</td></tr>',
          '  </tbody>',
          '</table>',
          '{{sample:accept}}',
          '<p>A call that does not fit the status of the request is refused with <code>409 invalid_status</code>, the request is not changed.</p>'
        ].join('\n')
      },
      {
        id: 'credits',
        title: 'Credits',
        body: [
          '<p class="api-doc-endpoint"><span class="verb get">GET</span><code>/credits</code></p>',
          '<p>Graphical requests are paid with the credits of your account (bought in the dashboard, Account &gt; My account &gt; Credits). <code>balance</code> is what the account holds, <code>available</code> is the balance minus what the open requests will cost. <code>prices</code> are the credits of each type at the standard tariff scale (1), and the urgent supplement.</p>',
          '{{sample:credits}}',
          '<p>The price of a request is <code>unitCredits x tariffScale + urgentCredits</code>, shown in its <code>quote</code>. It is debited when the request is closed, never when it is canceled.</p>'
        ].join('\n')
      },
      {
        id: 'webhooks',
        title: 'Webhooks',
        body: [
          '<p>Set the URL of your endpoint in the <a href="#api/graphical-requests/keys">API keys &amp; webhook</a> tab, or through the API (<code>GET</code>, <code>PUT</code>, <code>DELETE /webhook</code>, <code>POST /webhook/ping</code>). We then <code>POST</code> an event to it at every change of every graphical request of your account - also those made in the dashboard.</p>',
          '{{sample:webhookSet}}',
          '<table class="api-doc-table">',
          '  <thead><tr><th>Event</th><th>When</th></tr></thead>',
          '  <tbody>',
          '    <tr><td><code>request.created</code></td><td>A request was created.</td></tr>',
          '    <tr><td><code>request.status_changed</code></td><td>The status changed (e.g. to <code>processing</code>).</td></tr>',
          '    <tr><td><code>request.action_required</code></td><td>We need an answer from you.</td></tr>',
          '    <tr><td><code>request.quote_updated</code></td><td>The tariff scale changed: a new quote waits for your approval.</td></tr>',
          '    <tr><td><code>request.message_received</code></td><td>A graphic designer wrote to you.</td></tr>',
          '    <tr><td><code>request.delivered</code></td><td>The result files are ready.</td></tr>',
          '    <tr><td><code>request.closed</code></td><td>The request is closed (the credits are debited).</td></tr>',
          '    <tr><td><code>request.canceled</code></td><td>The request is canceled.</td></tr>',
          '    <tr><td><code>ping</code></td><td>A test, sent on demand.</td></tr>',
          '  </tbody>',
          '</table>',
          '<p>The body is the event, with the request as it is at that moment in <code>data</code> (the same object as <code>GET /graphical-requests/{id}</code>):</p>',
          '{{sample:event}}',
          '<h4>Check the signature</h4>',
          '<p>Every call carries <code>X-Compuzz-Signature: t=&lt;unix time&gt;,v1=&lt;signature&gt;</code>, where the signature is the hex HMAC-SHA256 of <code>t + "." + body</code> with your secret. Compute it on the raw body, compare it in constant time, and refuse calls older than 5 minutes.</p>',
          '{{sample:verify}}',
          '<div class="api-doc-note"><i class="fa fa-info-circle"></i><div><b>Good practice.</b> Answer with a <code>2xx</code> within 10 seconds and do the work (downloads...) afterwards. Events are delivered within about a minute; one that your endpoint refuses is not sent again, and the same event can, rarely, arrive twice (use its <code>id</code>). After an outage, catch up with <code>GET /graphical-requests?updatedSince=...</code>.</div></div>'
        ].join('\n')
      },
      {
        id: 'errors',
        title: 'Errors',
        body: [
          '<p>An error comes with an HTTP status and a JSON body whose <code>code</code> does not change (test it in your code) and whose <code>message</code> explains it (show it in your logs):</p>',
          '{{sample:error}}',
          '<table class="api-doc-table">',
          '  <thead><tr><th>HTTP</th><th>code</th><th>Meaning</th></tr></thead>',
          '  <tbody>',
          '    <tr><td>400</td><td><code>invalid_type</code>, <code>invalid_output_format</code>, <code>missing_file</code>, <code>invalid_file</code>, <code>too_many_files</code>, <code>invalid_name</code>, <code>invalid_date</code>, <code>invalid_limit</code>, <code>missing_parameter</code>, <code>invalid_body</code>, <code>empty_message</code>, <code>invalid_url</code>...</td><td>Something in the call is wrong: correct it before retrying.</td></tr>',
          '    <tr><td>401</td><td><code>missing_api_key</code>, <code>invalid_api_key</code></td><td>No key, or a key that is unknown or revoked.</td></tr>',
          '    <tr><td>402</td><td><code>insufficient_credits</code></td><td>Top up the credits of the account.</td></tr>',
          '    <tr><td>403</td><td><code>forbidden</code></td><td>The API is not enabled for your account (or for the person who created the key): contact us.</td></tr>',
          '    <tr><td>404</td><td><code>not_found</code>, <code>file_not_found</code></td><td>No such request (or file) in your account.</td></tr>',
          '    <tr><td>409</td><td><code>invalid_status</code>, <code>request_finished</code></td><td>Not possible in the current status of the request.</td></tr>',
          '    <tr><td>429</td><td><code>rate_limited</code></td><td>Too many calls: wait <code>Retry-After</code> seconds.</td></tr>',
          '    <tr><td>5xx</td><td><code>internal_error</code>, <code>file_unavailable</code></td><td>A problem on our side: retry a bit later (with the same <code>Idempotency-Key</code> for a creation).</td></tr>',
          '  </tbody>',
          '</table>'
        ].join('\n')
      },
      {
        id: 'reference',
        title: 'Output formats',
        body: [
          '<p>Values of <code>outputFormat</code> (also given by <code>GET /catalog</code>, with the types and statuses). <code>outputFormatLabel</code> is what our graphic designers read.</p>',
          '{{formats}}',
          '<p>The full technical reference (every field, every answer) is also published as OpenAPI: <code>/compuzz/api-docs/4-graphical-requests-api</code>.</p>'
        ].join('\n')
      }
    ]
  };
});

